// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { SearchScopeType } from '@Types';
import {
    SearchResultsType,
    ObjectTypeV1 as ObjectType,
    CaseOutput,
    TaskOutput,
    ObservableOutput,
    TaskLogOutput,
    AlertOutput,
} from 'thehive-sdk';
import { ListItemsPerPageType } from '@Constants/pagination';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { CaseResultCard } from '@Organisms/Search/SearchResultCard/CaseResultCard/CaseResultCard';
import { TaskResultCard } from '@Organisms/Search/SearchResultCard/TaskResultCard/TaskResultCard';
import { ObservableResultCard } from '@Organisms/Search/SearchResultCard/ObservableResultCard/ObservableResultCard';
import { LogResultCard } from '@Organisms/Search/SearchResultCard/LogResultCard/LogResultCard';
import { AlertResultCard } from '@Organisms/Search/SearchResultCard/AlertResultCard/AlertResultCard';
import { JobResultCard } from '@Organisms/Search/SearchResultCard/JobResultCard/JobResultCard';
import { Space } from 'antd';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';

import './SearchResults.scss';

// to be reworked
const CardsType: Partial<Record<SearchScopeType, (result: SearchResultsType) => ReactElement>> = {
    [ObjectType.CASE]: (result) => <CaseResultCard data={result as CaseOutput} />,
    [ObjectType.TASK]: (result) => <TaskResultCard data={result as TaskOutput} />,
    [ObjectType.OBSERVABLE]: (result) => <ObservableResultCard data={result as ObservableOutput} />,
    [ObjectType.LOG]: (result) => <LogResultCard data={result as TaskLogOutput} />,
    [ObjectType.JOB]: (result) => <JobResultCard data={result} />,
    [ObjectType.ALERT]: (result) => <AlertResultCard data={result as AlertOutput} />,
};

interface SearchResultsProps {
    fieldScope?: SearchScopeType;
    resultList?: SearchResultsType[];
    countResults?: number;
    pagination: {
        page: number;
        itemsPerPage: ListItemsPerPageType;
        onPageChange: (page: number) => void;
        onItemsPerPageChange: (itemsPerPage: ListItemsPerPageType) => void;
    };
    loading?: boolean;
}

const SearchResults = ({
    fieldScope,
    resultList,
    countResults = 0,
    pagination,
    loading,
}: SearchResultsProps): ReactElement => {
    const i = useLocalMessage();

    const processedCount = countResults < 0 ? `${Math.abs(countResults)}+` : countResults;

    return (
        <div className="search-results">
            <div className="search-results__container">
                <div className="search-results__header">
                    <h3 className="search-results__title">{i('search.results.title')}</h3>
                    {fieldScope && (
                        <Space size="large" className="search-results-record">
                            <span className="search-results-record__count">
                                {processedCount} {i('search.results.records')}
                            </span>
                            <PaginationSize paginationConfig={pagination} />
                        </Space>
                    )}
                </div>
                <div className="search-results__list">
                    {fieldScope && (
                        <div className="search-results__list__content">
                            {resultList?.map((result, index): ReactElement | null => {
                                const type: SearchScopeType =
                                    fieldScope === ObjectType.ANY
                                        ? (`${(result as any)?._type}`.toLowerCase() as any)
                                        : fieldScope;

                                if (fieldScope) {
                                    return React.cloneElement(CardsType[type]?.(result) || <div />, {
                                        key: `${fieldScope}${index}`,
                                    });
                                }
                                return null;
                            })}
                        </div>
                    )}
                </div>
            </div>
            <div className="search-results__footer">
                {fieldScope && (
                    <PaginationBrowse count={countResults} paginationConfig={pagination} loading={loading} />
                )}
            </div>
        </div>
    );
};

export default SearchResults;
